import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import InputMask from "react-input-mask";
import { useNavigate } from "react-router-dom";
import config from "../../../config";
import { customerPortalLogin } from "modules/customer-portal/services/customerPortalAuth.service";
import {
  joinCustomerPortalWaitlist,
  fetchWaitlistConflictContext,
} from "modules/customer-portal/services/customerPortalWaitlist.service";
import { getCustomerPortalSession, setCustomerPortalSession } from "modules/customer-portal/constants/auth";
import { formatDurationHuman } from "modules/customer-portal/utils/bookingPayload";
import { waitlistServiceDisplayLines } from "modules/customer-portal/utils/waitlistDisplay";
import BerryCheckIcon from "./BerryCheckIcon";

const WAITLIST_WINDOW_MINUTES = {
  morning: 180,
  lunch: 120,
  afternoon: 180,
  evening: 180,
};

const WAITLIST_WINDOW_END_HOUR = {
  morning: 12,
  lunch: 14,
  afternoon: 17,
  evening: 20,
};

function salonNowParts(timezone) {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone || undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date());
    const get = (type) => parts.find((part) => part.type === type)?.value;
    return {
      date: `${get("year")}-${get("month")}-${get("day")}`,
      minutes: Number(get("hour")) * 60 + Number(get("minute")),
    };
  } catch {
    const now = moment();
    return {
      date: now.format("YYYY-MM-DD"),
      minutes: now.hours() * 60 + now.minutes(),
    };
  }
}

function windowStillOpenOnPreferredDate(windowKey, preferredDate, timezone) {
  const endHour = WAITLIST_WINDOW_END_HOUR[windowKey];
  if (endHour == null || !preferredDate) {
    return true;
  }

  const now = salonNowParts(timezone);
  if (preferredDate > now.date) {
    return true;
  }
  if (preferredDate < now.date) {
    return false;
  }

  return now.minutes < endHour * 60;
}

function waitlistDurationMinutes(context) {
  return Math.max(0, Math.round(Number(context?.durationMinutes) || 0));
}

function windowFitsAppointment(windowKey, durationMinutes) {
  if (!durationMinutes || durationMinutes <= 0) return true;
  const windowMinutes = WAITLIST_WINDOW_MINUTES[windowKey];
  if (!windowMinutes) return true;
  return durationMinutes <= windowMinutes;
}

const NOTIFY_OPTIONS = [
  {
    value: "email",
    label: "Email",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 6.5h16a1.5 1.5 0 0 1 1.5 1.5v10A1.5 1.5 0 0 1 20 19.5H4A1.5 1.5 0 0 1 2.5 18V8A1.5 1.5 0 0 1 4 6.5Z" stroke="currentColor" strokeWidth="1.6" />
        <path d="m3.5 8 8.5 6 8.5-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    value: "sms",
    label: "SMS",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="7" y="2.5" width="10" height="19" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
        <path d="M10 18.5h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    value: "both",
    label: "Both",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 3.5 13.8 8.7l5.5.5-4.2 3.6 1.3 5.3L12 15.8 7.6 18.1l1.3-5.3-4.2-3.6 5.5-.5L12 3.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    ),
  },
];

function WaitlistServiceBreakdown({ lines, totalDurationLabel }) {
  if (!Array.isArray(lines) || lines.length === 0) return null;

  return (
    <table className="booking-waitlist-breakdown">
      <thead>
        <tr>
          <th>Service</th>
          <th>Staff</th>
          <th>Time</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line, index) => (
          <tr key={`${line.name}-${index}`}>
            <td className="booking-waitlist-breakdown-service">{line.name}</td>
            <td className="booking-waitlist-breakdown-staff">{line.staff_name}</td>
            <td className="booking-waitlist-breakdown-time">{line.duration_label || "—"}</td>
          </tr>
        ))}
        {totalDurationLabel ? (
          <tr className="booking-waitlist-breakdown-total">
            <td colSpan={2}>Total duration</td>
            <td>{totalDurationLabel}</td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}

WaitlistServiceBreakdown.propTypes = {
  lines: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    staff_name: PropTypes.string,
    duration_label: PropTypes.string,
  })),
  totalDurationLabel: PropTypes.string,
};

function formatTimeOption(time) {
  if (!time) return null;
  const parsed = moment(time, ["HH:mm:ss", "HH:mm"], true);
  return parsed.isValid() ? parsed.format("h:mm A") : time;
}

function resolveWaitlistMonthName(context) {
  if (context?.monthName) return context.monthName;
  const monthKey = context?.preferredMonth || context?.preferredDate;
  if (!monthKey) return null;
  const parsed = moment(monthKey, ["YYYY-MM", "YYYY-MM-DD"], true);
  return parsed.isValid() ? parsed.format("MMMM") : null;
}

function resolveWaitlistMonthLabel(context) {
  if (context?.monthLabel) return context.monthLabel;
  const monthKey = context?.preferredMonth || context?.preferredDate;
  if (!monthKey) return context?.dateLabel || "";
  const parsed = moment(monthKey, ["YYYY-MM", "YYYY-MM-DD"], true);
  return parsed.isValid() ? parsed.format("MMMM YYYY") : (context?.dateLabel || "");
}

function buildPreferredTimeOptions(context) {
  const options = [];

  if (context?.prefilledTime && !context?.isFullDay) {
    options.push({
      value: context.prefilledTime.length > 5 ? context.prefilledTime.slice(0, 5) : context.prefilledTime,
      label: `${formatTimeOption(context.prefilledTime)} (requested)`,
    });
  }

  options.push({ value: "any", label: "Any time" });

  const monthName = resolveWaitlistMonthName(context);
  if (monthName) {
    options.push({
      value: "month",
      label: `Anytime During ${monthName}`,
    });
  }

  const durationMinutes = waitlistDurationMinutes(context);
  const preferredDate = context?.preferredDate;
  const timezone = context?.timezone;
  if (
    windowFitsAppointment("morning", durationMinutes) &&
    windowStillOpenOnPreferredDate("morning", preferredDate, timezone)
  ) {
    options.push({ value: "morning", label: "Morning (9am – 12pm)" });
  }
  if (
    windowFitsAppointment("lunch", durationMinutes) &&
    windowStillOpenOnPreferredDate("lunch", preferredDate, timezone)
  ) {
    options.push({ value: "lunch", label: "Lunch (12pm – 2pm)" });
  }
  if (
    windowFitsAppointment("afternoon", durationMinutes) &&
    windowStillOpenOnPreferredDate("afternoon", preferredDate, timezone)
  ) {
    options.push({ value: "afternoon", label: "Afternoon (2pm – 5pm)" });
  }
  if (
    windowFitsAppointment("evening", durationMinutes) &&
    windowStillOpenOnPreferredDate("evening", preferredDate, timezone)
  ) {
    options.push({ value: "evening", label: "Evening (5pm – 8pm)" });
  }

  return options;
}

const WaitlistProgress = ({ step }) => (
  <div className="booking-waitlist-progress" aria-label={`Step ${step} of 3`}>
    <div className="booking-waitlist-progress-label">Step {step} of 3</div>
    <div className="booking-waitlist-progress-bars">
      {[1, 2, 3].map((bar) => (
        <span
          key={`waitlist-bar-${bar}`}
          className={`booking-waitlist-progress-bar${step >= bar ? " is-filled" : ""}`}
        />
      ))}
    </div>
  </div>
);

WaitlistProgress.propTypes = {
  step: PropTypes.number.isRequired,
};

function getJoinedWaitlistId(joinResult) {
  const id = Number(
    joinResult?.waitlist?.id ??
      joinResult?.data?.waitlist?.id ??
      joinResult?.waitlist_id ??
      0
  );
  return id > 0 ? id : null;
}

const BookingWaitlistModal = ({
  show,
  context,
  salonId,
  socialLoginEnabled = false,
  onClose,
  onSuccess,
  onCreateAccount,
}) => {
  const navigate = useNavigate();
  const isCustomerPortal = true;

  const [step, setStep] = useState(1);
  const [confirmed, setConfirmed] = useState(false);
  const [preferredTime, setPreferredTime] = useState("any");
  const [notifyVia, setNotifyVia] = useState("email");
  const [keepOriginalAppt, setKeepOriginalAppt] = useState(true);
  const [sameDayBooking, setSameDayBooking] = useState(null);
  const [joinResult, setJoinResult] = useState(null);

  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const preferredTimeOptions = useMemo(() => buildPreferredTimeOptions(context), [context]);

  useEffect(() => {
    if (!show) return;

    setStep(1);
    setConfirmed(false);
    setJoinResult(null);
    setSameDayBooking(null);
    setSubmitError("");
    setAuthError("");
    setKeepOriginalAppt(true);
    setNotifyVia("email");
    setMobile("");
    setPassword("");
    setShowPassword(false);

    if (context?.isFullDay) {
      setPreferredTime("any");
    } else if (context?.prefilledTime) {
      const time = context.prefilledTime.length > 5
        ? context.prefilledTime.slice(0, 5)
        : context.prefilledTime;
      setPreferredTime(time);
    } else {
      setPreferredTime("any");
    }
  }, [show, context]);

  useEffect(() => {
    if (!show) return;
    if (!preferredTimeOptions.some((option) => option.value === preferredTime)) {
      setPreferredTime("any");
    }
  }, [show, preferredTime, preferredTimeOptions]);

  const preferredTimeLabel = useMemo(() => {
    const option = preferredTimeOptions.find((item) => item.value === preferredTime);
    return option?.label?.replace(" (requested)", "") || "Any time";
  }, [preferredTime, preferredTimeOptions]);

  const isMonthPreferred = preferredTime === "month";
  const summaryDateLabel = useMemo(() => {
    if (!isMonthPreferred) return context?.dateLabel || "";
    return (
      joinResult?.waitlist?.date_label ||
      joinResult?.date_label ||
      resolveWaitlistMonthLabel(context)
    );
  }, [context, isMonthPreferred, joinResult]);
  const summaryPreferredTimeLabel = useMemo(() => {
    if (!isMonthPreferred) return preferredTimeLabel;
    return (
      joinResult?.waitlist?.preferred_time_label ||
      joinResult?.preferred_time_label ||
      preferredTimeLabel
    );
  }, [isMonthPreferred, joinResult, preferredTimeLabel]);

  const notifyLabel = NOTIFY_OPTIONS.find((item) => item.value === notifyVia)?.label || "Email";
  const durationMinutes = waitlistDurationMinutes(context);
  const durationLabel = formatDurationHuman(durationMinutes);
  const serviceDisplayLines = useMemo(() => {
    const fromContext = waitlistServiceDisplayLines(context);
    if (!confirmed) return fromContext;
    const fromJoin = waitlistServiceDisplayLines(joinResult?.waitlist || {});
    if (fromJoin.length === 0) return fromContext;
    return fromJoin.map((line, index) => ({
      ...line,
      duration_label: line.duration_label || fromContext[index]?.duration_label || "",
      staff_name: line.staff_name || fromContext[index]?.staff_name || "Any staff",
    }));
  }, [confirmed, context, joinResult]);
  const hiddenWindowNote = (() => {
    if (!durationLabel) return "";
    if (durationMinutes > WAITLIST_WINDOW_MINUTES.morning) {
      return `Your appointment is ${durationLabel}, so day-time windows are unavailable. Choose Any time or Anytime During the month.`;
    }
    const morningPassed = !windowStillOpenOnPreferredDate("morning", context?.preferredDate, context?.timezone);
    if (morningPassed) {
      return "Morning has already passed for this date, so it is not available.";
    }
    if (durationMinutes > WAITLIST_WINDOW_MINUTES.lunch) {
      return `Your appointment is ${durationLabel}, so Lunch (12pm – 2pm) is not available.`;
    }
    return "";
  })();

  if (!show || !context) {
    return null;
  }

  const resolvePreferredTimePayload = () => {
    if (preferredTime === "any") return null;
    return preferredTime;
  };

  const getAuthToken = () => getCustomerPortalSession()?.token;

  const loadConflictAndAdvance = async (token) => {
    if (context?.replaceAppointmentId) {
      await submitJoin(token);
      return;
    }
    try {
      const response = await fetchWaitlistConflictContext(
        token,
        context.preferredDate,
        context.serviceId,
        context.replaceAppointmentId
      );
      const conflict = response.data;
      if (conflict?.appointment_id) {
        setSameDayBooking(conflict);
        setStep(3);
        return;
      }
    } catch {
      // Proceed without conflict step if check fails
    }
    await submitJoin(token);
  };

  const submitJoin = async (token) => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const isRescheduleJoin = Boolean(context?.replaceAppointmentId) || context?.bookingType === "reschedule";
      const preferredTimeValue = resolvePreferredTimePayload();
      const response = await joinCustomerPortalWaitlist(token, {
        service_id: context.serviceId,
        staff_id: context.staffId || undefined,
        services: Array.isArray(context.services) && context.services.length > 0
          ? context.services
          : undefined,
        preferred_date: context.preferredDate,
        preferred_time: preferredTimeValue,
        ...(preferredTimeValue === "month" ? { preferred_scope: "month" } : {}),
        notify_via: notifyVia,
        keep_original_appt: isRescheduleJoin ? false : keepOriginalAppt,
        replace_appointment_id: context?.replaceAppointmentId || undefined,
        booking_type: isRescheduleJoin ? "reschedule" : (isCustomerPortal ? "portal" : "Widget"),
      });

      setJoinResult(response.data);
      setConfirmed(true);
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (err) {
      setSubmitError(
        err?.response?.data?.message ||
        err?.message ||
        "Unable to join waitlist. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinueStep1 = async () => {
    if (!notifyVia) return;

    // Customer portal users are already authenticated — skip forced re-login.
    if (isCustomerPortal) {
      const token = getAuthToken();
      if (token) {
        setSubmitting(true);
        setSubmitError("");
        try {
          await loadConflictAndAdvance(token);
        } finally {
          setSubmitting(false);
        }
        return;
      }
    }

    // Public booking link (or missing session): require sign-in.
    setStep(2);
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    setAuthError("");

    if (!mobile.trim() || !password) {
      setAuthError("Mobile number and password are required.");
      return;
    }
    if (!config.phone_number_pattern.test(mobile.trim())) {
      setAuthError("Please enter a valid mobile number (9999-999-999).");
      return;
    }

    setSubmitting(true);
    try {
      const response = await customerPortalLogin({
        phone_number: mobile.trim(),
        country_code: config.default_country_code,
        password,
        salon_id: salonId,
      });
      setCustomerPortalSession(response.data);
      await loadConflictAndAdvance(response.data.token);
    } catch (err) {
      setAuthError(
        err?.response?.data?.message ||
        err?.message ||
        "Unable to sign in. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmWaitlist = async () => {
    const token = getAuthToken();
    if (!token) {
      setStep(2);
      return;
    }
    await submitJoin(token);
  };

  const handleSocialSignIn = () => {
    setAuthError("Social sign-in is not available yet. Please use mobile number and password.");
  };

  const handleViewMyWaitlist = () => {
    const waitlistId = getJoinedWaitlistId(joinResult);
    const session = getCustomerPortalSession();
    const query = new URLSearchParams();
    if (waitlistId) {
      query.set("waitlist_id", String(waitlistId));
    }
    if (session?.user?.id) {
      query.set("user_id", String(session.user.id));
    }
    const qs = query.toString();
    navigate(`/waitlist${qs ? `?${qs}` : ""}`);
  };

  if (confirmed) {
    const position = joinResult?.position ?? joinResult?.waitlist?.position;

    return (
      <div className="booking-signin-modal-backdrop" onClick={onClose} role="presentation">
        <div
          className="booking-signin-modal booking-waitlist-modal booking-waitlist-modal--success"
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <div className="booking-waitlist-success-icon">
            <BerryCheckIcon size={64} className="booking-waitlist-success-check" />
          </div>
          <h2 className="booking-waitlist-title">You are on the waitlist</h2>
          <p className="booking-waitlist-subtitle">We will notify you when a spot opens</p>

          <div className="booking-waitlist-summary-card">
            {serviceDisplayLines.length > 0 ? (
              <WaitlistServiceBreakdown
                lines={serviceDisplayLines}
                totalDurationLabel={joinResult?.waitlist?.duration_label || durationLabel}
              />
            ) : (
              <p className="booking-waitlist-context">{context.serviceName}</p>
            )}
            <div className="booking-waitlist-summary-meta">
              <div className="booking-waitlist-summary-meta-item">
                <span>Date</span>
                <strong>{summaryDateLabel}</strong>
              </div>
              <div className="booking-waitlist-summary-meta-item">
                <span>Preferred time</span>
                <strong>{summaryPreferredTimeLabel}</strong>
              </div>
              {position ? (
                <div className="booking-waitlist-summary-meta-item booking-waitlist-summary-meta-item--position">
                  <span>Position</span>
                  <strong>#{position}</strong>
                </div>
              ) : null}
              <div className="booking-waitlist-summary-meta-item">
                <span>Notify via</span>
                <strong>{notifyLabel}</strong>
              </div>
            </div>
          </div>

          <div className="booking-waitlist-actions">
            <button
              type="button"
              className="booking-waitlist-btn booking-waitlist-btn--primary"
              onClick={handleViewMyWaitlist}
            >
              View my waitlist
            </button>
            <button type="button" className="booking-waitlist-btn booking-waitlist-btn--ghost" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-signin-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="booking-signin-modal booking-waitlist-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-waitlist-title"
      >
        <WaitlistProgress step={step} />

        {step === 1 ? (
          <>
            <h2 id="booking-waitlist-title" className="booking-waitlist-title">Join waitlist</h2>
            {serviceDisplayLines.length > 0 ? (
              <WaitlistServiceBreakdown
                lines={serviceDisplayLines}
                totalDurationLabel={durationLabel}
              />
            ) : (
              <p className="booking-waitlist-context">
                {`${context.serviceName} with ${context.staffName}`}
              </p>
            )}

            <div className="booking-waitlist-field">
              <label htmlFor="waitlist-preferred-time">Preferred time</label>
              <div className="booking-waitlist-select-wrap">
                <select
                  id="waitlist-preferred-time"
                  className="booking-waitlist-select"
                  value={preferredTime}
                  onChange={(event) => setPreferredTime(event.target.value)}
                >
                  {preferredTimeOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              {hiddenWindowNote ? (
                <p className="booking-waitlist-window-note">{hiddenWindowNote}</p>
              ) : null}
            </div>

            <div className="booking-waitlist-field">
              <span className="booking-waitlist-field-label">Notify me via</span>
              <div className="booking-waitlist-toggle-group" role="group" aria-label="Notify me via">
                {NOTIFY_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`booking-waitlist-toggle${notifyVia === option.value ? " is-active" : ""}`}
                    onClick={() => setNotifyVia(option.value)}
                    aria-pressed={notifyVia === option.value}
                  >
                    <span className="booking-waitlist-toggle-icon">{option.icon}</span>
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="booking-waitlist-alert" role="note">
              You have 15 minutes to confirm when a spot opens.
            </div>

            <div className="booking-waitlist-actions">
              <button
                type="button"
                className="booking-waitlist-btn booking-waitlist-btn--primary"
                disabled={!notifyVia || submitting}
                onClick={handleContinueStep1}
              >
                Continue
              </button>
              <button type="button" className="booking-waitlist-btn booking-waitlist-btn--ghost" onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        ) : null}

        {step === 2 ? (
          <>
            <h2 id="booking-waitlist-title" className="booking-waitlist-title">Sign in to continue</h2>
            <p className="booking-waitlist-subtitle">Your spot will be held once you&apos;re logged in</p>

            <form className="booking-waitlist-form" onSubmit={handleSignIn}>
              <div className="booking-waitlist-field">
                <label htmlFor="waitlist-mobile">Mobile number</label>
                <InputMask
                  id="waitlist-mobile"
                  className="booking-waitlist-input"
                  mask={config.phone_number_mask}
                  maskChar={null}
                  type="tel"
                  value={mobile}
                  onChange={(event) => setMobile(event.target.value)}
                  placeholder="9999-999-999"
                  autoComplete="tel"
                />
              </div>

              <div className="booking-waitlist-field">
                <label htmlFor="waitlist-password">Password</label>
                <div className="booking-waitlist-password-wrap">
                  <input
                    id="waitlist-password"
                    className="booking-waitlist-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="booking-waitlist-password-toggle"
                    onClick={() => setShowPassword((current) => !current)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              {authError ? (
                <span className="booking-waitlist-error" role="alert">{authError}</span>
              ) : null}

              <button type="submit" className="booking-waitlist-btn booking-waitlist-btn--primary" disabled={submitting}>
                {submitting ? "Signing in…" : "Sign in"}
              </button>
            </form>

            {socialLoginEnabled ? (
              <>
                <div className="booking-waitlist-divider" role="presentation">
                  <span>or</span>
                </div>

                <div className="booking-waitlist-social">
                  <button type="button" className="booking-waitlist-btn booking-waitlist-btn--social" onClick={handleSocialSignIn}>
                    <span className="booking-waitlist-social-icon booking-waitlist-social-icon--google" aria-hidden="true">G</span>
                    Continue with Google
                  </button>
                  <button type="button" className="booking-waitlist-btn booking-waitlist-btn--social" onClick={handleSocialSignIn}>
                    <span className="booking-waitlist-social-icon booking-waitlist-social-icon--facebook" aria-hidden="true">f</span>
                    Continue with Facebook
                  </button>
                </div>
              </>
            ) : null}

            <div className="booking-waitlist-divider" role="presentation">
              <span>new here?</span>
            </div>

            <button
              type="button"
              className="booking-waitlist-btn booking-waitlist-btn--ghost"
              onClick={() => {
                if (onCreateAccount) onCreateAccount();
              }}
            >
              Create an account
            </button>

            <button type="button" className="booking-waitlist-back-link" onClick={() => setStep(1)}>
              Back
            </button>
          </>
        ) : null}

        {step === 3 ? (
          <>
            <h2 id="booking-waitlist-title" className="booking-waitlist-title">One more thing</h2>
            <p className="booking-waitlist-subtitle">You already have a booking</p>

            <div className="booking-waitlist-alert booking-waitlist-alert--conflict" role="note">
              {sameDayBooking ? (
                <>
                  You have <strong>{sameDayBooking.service_name || "a service"}</strong>
                  {sameDayBooking.date_label || sameDayBooking.time_label
                    ? ` booked on ${[sameDayBooking.date_label, sameDayBooking.time_label].filter(Boolean).join(" at ")}`
                    : ""}
                  . What would you like to do if your waitlist spot is confirmed?
                </>
              ) : (
                <>What would you like to do if your waitlist spot is confirmed?</>
              )}
            </div>

            <div className="booking-waitlist-options" role="radiogroup" aria-label="Existing booking preference">
              <button
                type="button"
                className={`booking-waitlist-option${keepOriginalAppt ? " is-active" : ""}`}
                onClick={() => setKeepOriginalAppt(true)}
                aria-pressed={keepOriginalAppt}
              >
                <strong>Keep my existing booking</strong>
                <span>I&apos;ll decide when I&apos;m notified</span>
              </button>

              <button
                type="button"
                className={`booking-waitlist-option${!keepOriginalAppt ? " is-active" : ""}`}
                onClick={() => setKeepOriginalAppt(false)}
                aria-pressed={!keepOriginalAppt}
              >
                <strong>Replace my existing booking</strong>
                <span>Cancel it when this waitlist spot is confirmed</span>
              </button>
            </div>

            {submitError ? (
              <span className="booking-waitlist-error" role="alert">{submitError}</span>
            ) : null}

            <div className="booking-waitlist-actions">
              <button
                type="button"
                className="booking-waitlist-btn booking-waitlist-btn--primary"
                disabled={submitting}
                onClick={handleConfirmWaitlist}
              >
                {submitting ? "Joining…" : "Confirm waitlist"}
              </button>
              <button type="button" className="booking-waitlist-btn booking-waitlist-btn--ghost" onClick={() => setStep(1)}>
                Back
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

BookingWaitlistModal.propTypes = {
  show: PropTypes.bool.isRequired,
  context: PropTypes.shape({
    serviceId: PropTypes.number.isRequired,
    staffId: PropTypes.number,
    serviceName: PropTypes.string.isRequired,
    serviceNames: PropTypes.arrayOf(PropTypes.string),
    services: PropTypes.arrayOf(PropTypes.shape({
      service_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      staff_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      staff_name: PropTypes.string,
    })),
    staffName: PropTypes.string.isRequired,
    preferredDate: PropTypes.string.isRequired,
    dateLabel: PropTypes.string.isRequired,
    preferredMonth: PropTypes.string,
    monthName: PropTypes.string,
    monthLabel: PropTypes.string,
    timezone: PropTypes.string,
    durationMinutes: PropTypes.number,
    prefilledTime: PropTypes.string,
    isFullDay: PropTypes.bool,
    replaceAppointmentId: PropTypes.number,
    bookingType: PropTypes.string,
  }),
  salonId: PropTypes.number,
  socialLoginEnabled: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  onCreateAccount: PropTypes.func,
};

export default BookingWaitlistModal;
